import { writable } from "svelte/store";
import { fetchWithSession } from "../util/fetchWithSession.js";

export const DEFAULT_UI_FEATURES = {
  mainAreas: {
    taskMiningProjects: true,
    potentialScoutProjects: true,
    systemIntelligence: true,
    processIntelligence: true,
    companySettings: true,
    showAllProjectsAsArchived: false,
  },
  projectProgress: {
    blacklisting: true,
    startWhitelistPhase: true,
    startTaskRegistration: true,
    checkAnonymization: true,
    numberAssistant: true,
    discoveryExtended: true,
    // Karte "Preprocess Data" in der Phase Extended
    preprocessData: true,
  },
  analysisViews: {
    appDrilldown: true,
    appFlow: true,
    workloads: true,
    shortcutAnalysis: true,
    processExplorer: true,
    caseExplorer: true,
    kpiMatrix: true,
    changeAnalysis: true,
    changeAnalysisDemoModels: false,
  },
  platformFeatures: {
    newProject: true,
    guidedTours: true,
    registrationsGlobalSwitch: true,
    enableBlacklistRecording: true,
    demoSoftwareIntelligence: false,
    frameworkAssistant: true,
    metricsFilter: true,
    // Main: Fenstertitel und UI-Elemente in Treemap, App Flow, Copy-Paste
    mainWindowTitles: true,
    // Registrierungen: Bedienelemente unter den Fenstertiteln (Tooltips, dritte Ebene)
    registrationUiElements: true,
    // Digital Twin > Software Intelligence: Eintrag "License Catalog"
    licenseCatalog: true,
  },
  userMenu: {
    showAccountSettings: true,
  },
  processExplorer: { aiBot: true, filters: true },
  kpiMatrix: { taskKpiSetup: true },
  exports: {
    image: true,
    bpmn: true,
    reportPdf: true,
    actionPlan: true,
    pdd: true,
    sop: true,
    createWorkflowN8n: true,
  },
};

export const uiFeatureStore = writable(DEFAULT_UI_FEATURES);

function mergeUiFeatures(features = {}) {
  return {
    ...DEFAULT_UI_FEATURES,
    ...features,
    mainAreas: {
      ...DEFAULT_UI_FEATURES.mainAreas,
      ...(features.mainAreas || {}),
    },
    projectProgress: {
      ...DEFAULT_UI_FEATURES.projectProgress,
      ...(features.projectProgress || {}),
    },
    platformFeatures: {
      ...DEFAULT_UI_FEATURES.platformFeatures,
      ...(features.platformFeatures || {}),
    },
    userMenu: {
      ...DEFAULT_UI_FEATURES.userMenu,
      ...(features.userMenu || {}),
    },
    analysisViews: {
      ...DEFAULT_UI_FEATURES.analysisViews,
      ...(features.analysisViews || {}),
    },
    processExplorer: {
      ...DEFAULT_UI_FEATURES.processExplorer,
      ...(features.processExplorer || {}),
    },
    kpiMatrix: {
      ...DEFAULT_UI_FEATURES.kpiMatrix,
      ...(features.kpiMatrix || {}),
    },
    exports: {
      ...DEFAULT_UI_FEATURES.exports,
      ...(features.exports || {}),
    },
  };
}

export async function loadUiFeatures() {
  try {
    const response = await fetchWithSession("/api/damprojects/ui-features");
    if (!response.ok) throw new Error("Could not load UI settings.");
    const features = mergeUiFeatures(await response.json());
    uiFeatureStore.set(features);
    return features;
  } catch (error) {
    console.error(error);
    return DEFAULT_UI_FEATURES;
  }
}

export async function saveUiFeatures(features) {
  const response = await fetchWithSession("/api/damprojects/ui-features", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(features),
  });
  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = errorText;
    try {
      errorMessage = JSON.parse(errorText).message || errorText;
    } catch {
      // Keep the plain response text when the backend does not return JSON.
    }
    throw new Error(
      `Could not save UI settings (${response.status}): ${errorMessage || response.statusText}`,
    );
  }
  const responseText = await response.text();
  const savedFeatures = responseText
    ? mergeUiFeatures(JSON.parse(responseText))
    : mergeUiFeatures(features);
  uiFeatureStore.set(savedFeatures);
  return savedFeatures;
}
